<template>
  <div class="page-vendors">
    <section class="mx-auto w-full max-w-10xl px-4 py-10 lg:px-8">
      <div class="space-y-4">
        <div class="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-card">
          <div class="flex items-center gap-3 text-sm font-semibold">
            <button
              class="flex items-center gap-2 rounded-full px-3 py-2 transition"
              :class="viewMode === 'list' ? 'text-brand bg-brand/10' : 'text-ink hover:text-brand'"
              @click="viewMode = 'list'"
            >
              <i class="ri-list-check-2 text-base"></i>
              List
            </button>
            <button
              class="flex items-center gap-2 rounded-full px-3 py-2 transition"
              :class="viewMode === 'grid' ? 'text-brand bg-brand/10' : 'text-ink hover:text-brand'"
              @click="viewMode = 'grid'"
            >
              <i class="ri-layout-grid-fill text-base"></i>
              Grid
            </button>
            <button
              class="flex items-center gap-2 rounded-full px-3 py-2 transition"
              :class="viewMode === 'map' ? 'text-brand bg-brand/10' : 'text-ink hover:text-brand'"
              @click="viewMode = 'map'"
            >
              <i class="ri-map-pin-2-line text-base"></i>
              Map
            </button>
          </div>
          <button
            class="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white shadow-card transition hover:-translate-y-0.5 hover:bg-brandDark"
            @click="openFilters"
          >
            <i class="ri-equalizer-line text-base"></i>
            Filters
          </button>
        </div>

        <div
          class="grid gap-6"
          :class="viewMode === 'map' ? 'grid-cols-1' : 'lg:grid-cols-[1.7fr_0.9fr] lg:items-start'"
        >
          <div v-show="viewMode !== 'map'" class="space-y-4">
            <div v-if="loading" class="rounded-2xl border border-dashed border-gray-200 bg-white/70 p-6 text-sm font-semibold text-muted">Loading vendors...</div>
            <div v-else-if="error" class="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
              {{ error }}
            </div>
            <div v-else :class="cardGridClass">
              <article
                v-for="vendor in vendors"
                :key="vendor.id || vendor.vendor_id || vendor.business_id || vendor.name"
                class="rounded-2xl bg-white shadow-card ring-1 ring-gray-100 transition hover:-translate-y-1 hover:shadow-lg"
                :class="viewMode === 'list' ? 'flex flex-col gap-3 p-4 md:flex-row md:p-5' : 'p-0 overflow-hidden'"
              >
                <div :class="viewMode === 'list' ? 'w-full md:w-56 flex-shrink-0 aspect-[4/3]' : 'w-full aspect-[4/3]'">
                  <div class="relative h-full w-full overflow-hidden rounded-xl bg-gray-100">
                    <img :src="getVendorImage(vendor)" :alt="getVendorName(vendor)" class="h-full w-full object-cover">
                    <span class="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white shadow-card" title="Showcases Sponsored, Top rated vendor overall">
                      <i class="ri-vip-crown-fill text-sm"></i>
                      Handpicked
                    </span>
                    <div v-if="viewMode === 'grid'" class="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-xs font-semibold text-white">
                      <i class="ri-camera-fill"></i>
                    </div>
                  </div> 
                </div>

                <div :class="viewMode === 'list' ? 'flex flex-1 flex-col gap-2' : 'space-y-3 p-4'">
                  <div class="flex flex-wrap items-start gap-3">
                    <div class="flex-1"> 
                      <h3 class="text-lg font-semibold text-ink">{{ formatVendorDisplayName(vendor) }}</h3>
                      <p class="text-sm text-muted">
                        <i class="ri-map-pin-2-line text-brand"></i>
                        {{ vendor.s_city || vendor.city || vendor.s_address || 'Location unavailable' }}
                      </p>
                    </div>
                    <div class="flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1 text-xs font-semibold text-ink">
                      <i class="ri-star-fill text-brand"></i>
                      <span>{{ getRating(vendor) }}</span>
                      <span class="text-muted">({{ getReviews(vendor) }} reviews)</span>
                    </div>
                  </div>

                  <div class="space-y-2"> 
                    <div class="flex flex-wrap gap-2">
                      <span
                        v-for="(cat, idx) in displayedCategories(vendor)"
                        :key="idx"
                        class="rounded-md bg-gray-100 px-2 py-1 text-xs font-semibold text-ink"
                      >
                        {{ cat }}
                      </span>
                      <button
                        v-if="hasMoreCategories(vendor)"
                        class="text-xs font-semibold text-brand underline"
                        @click.stop="toggleCategories(vendor)"
                      >
                        {{ isExpanded(vendor) ? 'Less' : `+${remainingCategoriesCount(vendor)}` }}
                      </button>
                    </div>
                  </div> 

                  <div class="mt-auto flex justify-end">
                    <a
                      class="inline-flex items-center justify-center rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white shadow-card transition hover:-translate-y-0.5 hover:bg-brandDark"
                      :href="getVendorLink(vendor)"
                    >
                      <i class="ri-arrow-right-s-line mr-2 text-base"></i>
                      View
                    </a>
                  </div>
                </div>
              </article>
              <div v-if="!vendors.length" class="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-muted">No vendors found.</div>
            </div>
          </div>

          <div
            class="map-wrapper w-full overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-gray-100"
            :class="viewMode === 'map' ? 'h-[520px]' : 'sticky top-4 h-[420px] lg:top-6 lg:h-[75vh] lg:max-w-[520px]'"
          >
            <div id="vendor-map" class="h-full w-full"></div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <transition name="fade">
    <div
      v-if="showFilterModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      @click.self="closeFilters"
    >
      <div class="w-full max-w-3xl rounded-[28px] bg-white p-6 shadow-2xl">
        <div class="flex items-start justify-between gap-4">
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-muted">Filters</p>
            <h3 class="text-xl font-semibold text-ink">Refine your search</h3>
          </div>
          <button class="rounded-full border border-gray-200 p-2 text-muted transition hover:border-brand hover:text-brand" aria-label="Close filters" @click="closeFilters">
            <i class="ri-close-line text-lg"></i>
          </button>
        </div>

        <div class="mt-6 grid gap-6 md:grid-cols-2">
          <div class="space-y-3">
            <p class="text-xs font-semibold uppercase text-muted">Category</p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="cat in categories"
                :key="cat"
                class="rounded-full border px-3 py-2 text-sm font-semibold transition"
                :class="selectedCategory === cat ? 'border-brand bg-brand/10 text-brand' : 'border-gray-200 bg-white text-ink hover:border-brand hover:text-brand'"
                @click="selectedCategory = cat"
              >
                {{ cat }}
              </button>
            </div>
          </div>

          <div class="space-y-3">
            <p class="text-xs font-semibold uppercase text-muted">Amenities</p>
            <div class="grid grid-cols-2 gap-2">
              <label
                v-for="amenity in amenities"
                :key="amenity"
                class="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-ink"
              >
                <input v-model="selectedAmenities" :value="amenity" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand">
                {{ amenity }}
              </label>
            </div>
          </div>
        </div>

        <div class="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="resetFilters">Reset</button>
          <div class="flex gap-3">
            <button class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="closeFilters">Cancel</button>
            <button class="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-card transition hover:bg-brandDark" @click="applyFilters">Apply filters</button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import api from '@/services/user/api'
import { useSearchStore } from '@/stores/search'

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

const searchStore = useSearchStore()
const route = useRoute()

const vendors = ref([])
const loading = ref(true)
const error = ref('')
const map = ref(null)
const markers = ref([])
const locationRequested = ref(false)
const viewMode = ref('list')
const showFilterModal = ref(false)
const selectedCategory = ref('All')
const amenities = ['Catering', 'Decor', 'Photography', 'Planning']
const selectedAmenities = ref([])
const categories = ['All', 'Venues', 'Planning', 'Photography', 'Catering']
const expandedCategories = ref(new Set())
const cardGridClass = computed(() => viewMode.value === 'list' ? 'grid gap-4' : 'grid gap-4 md:grid-cols-3')

const loadVendors = async () => {
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('category_id', searchStore.categoryId || '')
    formData.append('lat', searchStore.lat ?? '')
    formData.append('long', searchStore.lng ?? '')
    const { data } = await api.getVendors(formData)
    vendors.value = data?.list || data?.data || data?.vendors || []
  } catch (err) {
    console.error(err)
    error.value = 'Failed to load vendors.'
    vendors.value = []
  } finally {
    loading.value = false
  }
}

const ensureMap = () => {
  if (map.value) return
  map.value = L.map('vendor-map').setView([20.5937, 78.9629], 5)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors'
  }).addTo(map.value)
}

const dropMarkers = () => {
  if (!map.value) return
  markers.value.forEach((m) => m.remove())
  markers.value = []

  let anyPin = false
  vendors.value.forEach((vendor) => {
    const coords = extractCoords(vendor)
    if (coords) {
      const marker = L.marker([coords.lat, coords.lng]).addTo(map.value)
      const popupHtml = `
        <div style="width:220px;display:flex;gap:10px;align-items:flex-start;">
          <div style="flex-shrink:0;width:56px;height:56px;border-radius:12px;overflow:hidden;background:#f3f4f6;">
            <img src="${getVendorImage(vendor)}" alt="${getVendorName(vendor)}" style="width:100%;height:100%;object-fit:cover;" />
          </div>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;font-size:14px;line-height:1.3;margin-bottom:2px;">${formatVendorDisplayName(vendor)}</div>
            <div style="font-size:12px;color:#6b7280;margin-bottom:6px;">${getVendorCategory(vendor)}</div>
            <a href="${getVendorLink(vendor)}" style="font-size:12px;color:#2563eb;font-weight:600;text-decoration:none;">View vendor →</a>
          </div>
        </div>
      `
      marker.bindPopup(popupHtml)
      markers.value.push(marker)
      anyPin = true
    }
  })

  if (anyPin) {
    const group = L.featureGroup(markers.value)
    map.value.fitBounds(group.getBounds().pad(0.2))
  } else {
    map.value.setView([20.5937, 78.9629], 5)
  }
}

onMounted(async () => {
  hydrateFromQuery()
  ensureMap()
  await ensureLocation()
  await loadVendors()
  dropMarkers()
})

watch(
  () => [searchStore.categoryId],
  async () => {
    await loadVendors()
    dropMarkers()
  }
)

watch(vendors, () => dropMarkers())

watch(
  () => route.query,
  async () => {
    hydrateFromQuery()
    await loadVendors()
    dropMarkers()
  },
  { deep: true }
)

watch(viewMode, async () => {
  if (!map.value) return
  await nextTick()
  map.value.invalidateSize()
  dropMarkers()
})

const extractCoords = (vendor) => {
  if (vendor.lat != null && vendor.lng != null) return { lat: vendor.lat, lng: vendor.lng }
  if (vendor.latitude != null && vendor.longitude != null) return { lat: vendor.latitude, lng: vendor.longitude }
  const directionLink = (vendor.links || []).find((l) => (l.title || '').toLowerCase().includes('direction'))
  const val = directionLink?.value || ''
  const [latStr, lngStr] = val.split(',')
  const latNum = parseFloat(latStr)
  const lngNum = parseFloat(lngStr)
  if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
    return { lat: latNum, lng: lngNum }
  }
  return null
}

const getVendorName = (vendor) => vendor.s_business || vendor.s_name || vendor.name || vendor.business_name || 'Vendor'
const getVendorCategory = (vendor) => (vendor.categories && vendor.categories[0]?.name) || vendor.business_category || 'Vendor'
const getVendorImage = (vendor) => vendor.s_user_img || vendor.image || 'https://dummyimage.com/200x200/e5e7eb/111827&text=Invi8+Vendor'
const getVendorLink = (vendor) => {
  const username = vendor.s_username || vendor.username || vendor.slug
  if (username) return `/vendor/${username}`
  if (vendor.id) return `/vendor/${vendor.id}`
  if (vendor.vendor_id) return `/vendor/${vendor.vendor_id}`
  const profileLink = (vendor.links || []).find((l) => (l.title || '').toLowerCase().includes('profile'))?.value
  return profileLink || '/vendor'
}
const formatVendorDisplayName = (vendor) => {
  const raw = getVendorName(vendor)
  const titled = (raw || '').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase())
  if (titled.length > 15) {
    return `${titled.slice(0, 15)}...`
  }
  return titled || 'Vendor'
} 
const getRating = (vendor) => vendor.rating || vendor.review_rating || '4.9'
const getReviews = (vendor) => vendor.reviews_count || vendor.reviews || '19' 
const getVendorId = (vendor) => vendor.s_id || vendor.id || vendor.vendor_id || vendor.business_id || vendor.name || ''
const getVendorCategories = (vendor) => (vendor.categories || []).map((cat) => cat.name).filter(Boolean)
const hasMoreCategories = (vendor) => getVendorCategories(vendor).length > 3
const isExpanded = (vendor) => expandedCategories.value.has(getVendorId(vendor))
const displayedCategories = (vendor) => {
  const cats = getVendorCategories(vendor)
  if (!cats.length) return ['General']
  if (isExpanded(vendor)) return cats
  return cats.slice(0, 3)
}
const remainingCategoriesCount = (vendor) => {
  const cats = getVendorCategories(vendor)
  return Math.max(0, cats.length - 3)
}
const toggleCategories = (vendor) => {
  const id = getVendorId(vendor)
  const next = new Set(expandedCategories.value)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  expandedCategories.value = next
}
const openFilters = () => { showFilterModal.value = true }
const closeFilters = () => { showFilterModal.value = false }
const resetFilters = () => {
  selectedCategory.value = 'All'
  selectedAmenities.value = []
}
const applyFilters = () => {
  closeFilters()
}

const hydrateFromQuery = () => {
  const { lat, lng, location, adults, children, infants, pets } = route.query
  const latNum = lat != null ? parseFloat(lat) : null
  const lngNum = lng != null ? parseFloat(lng) : null

  if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
    searchStore.setLocation({
      text: location || searchStore.locationText || 'Current location',
      lat: latNum,
      lng: lngNum
    })
  }

  const parsedGuests = {
    adults: Number.isFinite(Number(adults)) ? Number(adults) : undefined,
    children: Number.isFinite(Number(children)) ? Number(children) : undefined,
    infants: Number.isFinite(Number(infants)) ? Number(infants) : undefined,
    pets: Number.isFinite(Number(pets)) ? Number(pets) : undefined
  }

  if (Object.values(parsedGuests).some((v) => v !== undefined)) {
    const mergedGuests = { ...searchStore.guests }
    Object.entries(parsedGuests).forEach(([key, value]) => {
      if (value !== undefined) mergedGuests[key] = value
    })
    searchStore.setGuests(mergedGuests)
  }
}

const ensureLocation = async () => {
  if (locationRequested.value) return
  if (searchStore.lat != null && searchStore.lng != null) return
  locationRequested.value = true
  if (!navigator.geolocation) return
  await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        searchStore.setLocation({
          text: 'Current location',
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        })
        resolve()
      },
      () => resolve(),
      { enableHighAccuracy: true, timeout: 8000 }
    )
  })
}

</script>

<style scoped>
.map-wrapper {
  z-index: 1;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
